import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  CheckCheck,
  LogOut,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  Chat,
  ChatGroup,
  OnlineStaffUser,
  QuickReply,
  QuickReplyDirection,
} from "../types";
import { QuickRepliesPanel } from "./QuickRepliesPanel";
import { Avatar } from "./shared/Avatar";
import "./ChatList.css";
const sameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();
const weekStart = (value: Date) => {
  const result = new Date(value);
  const day = result.getDay();
  result.setDate(result.getDate() + (day === 0 ? -6 : 1 - day));
  result.setHours(0, 0, 0, 0);
  return result;
};
const time = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  const now = new Date();
  if (sameDay(date, now))
    return date.toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
    });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return "вчера";
  if (weekStart(date).getTime() === weekStart(now).getTime())
    return date
      .toLocaleDateString("ru-RU", { weekday: "short" })
      .replace(".", "");
  return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
};
const colors = [
  "violet",
  "blue",
  "cyan",
  "green",
  "amber",
  "orange",
  "red",
  "pink",
];

const ChatList: React.FC<{
  chats: Chat[];
  onlineUsers?: OnlineStaffUser[];
  groups: ChatGroup[];
  replies: QuickReply[];
  replyDirections?: QuickReplyDirection[];
  draftsByChat?: Record<string, string>;
  selectedChatId: string | null;
  loading: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onSearchChange?: (query: string) => void;
  onSelect: (id: string) => void;
  onLogout: () => void;
  onOpenTeacherAssistant?: () => void;
  onAssign: (chatId: string, ids: string[]) => Promise<void>;
  onCreateGroup: (name: string, color: string) => Promise<void>;
  onUpdateGroup?: (
    id: string,
    payload: Partial<Pick<ChatGroup, "name" | "color">>,
  ) => Promise<void>;
  onDeleteGroup: (id: string) => Promise<void>;
  onCreateTopic?: (directionId: string, name: string) => Promise<string>;
  onUpdateTopic?: (id: string, name: string) => Promise<void>;
  onDeleteTopic?: (id: string) => Promise<void>;
  onCreateReply: (topicId: string, text: string) => Promise<void>;
  onUpdateReply: (id: string, text: string) => Promise<void>;
  onDeleteReply: (id: string) => Promise<void>;
  onInsertReply: (text: string) => void;
  quickOpenRequest?: number;
}> = ({
  chats,
  onlineUsers = [],
  groups = [],
  replies = [],
  replyDirections = [],
  draftsByChat = {},
  selectedChatId,
  loading,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  onSearchChange,
  onSelect,
  onLogout,
  onOpenTeacherAssistant,
  onAssign,
  onCreateGroup,
  onUpdateGroup,
  onDeleteGroup,
  onCreateTopic,
  onUpdateTopic,
  onDeleteTopic,
  onCreateReply,
  onUpdateReply,
  onDeleteReply,
  onInsertReply,
  quickOpenRequest = 0,
}) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [menu, setMenu] = useState<{ chat: Chat; x: number; y: number } | null>(
    null,
  );
  const press = useRef<{ timer: number; x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const [newGroup, setNewGroup] = useState("");
  const [colorPickerId, setColorPickerId] = useState<string | null>(null);
  const [quickOpen, setQuickOpen] = useState(false);
  useEffect(() => {
    if (quickOpenRequest > 0) setQuickOpen(true);
  }, [quickOpenRequest]);
  const filtered = useMemo(
    () =>
      chats.filter(
        (chat) =>
          (filter === "all" ||
            (filter === "unread"
              ? chat.unreadCount > 0
              : filter === "none"
                ? !(chat.groups || []).length
                : (chat.groups || []).some((g) => g.id === filter))) &&
          `${chat.name} ${chat.context?.title || ""}`
            .toLowerCase()
            .includes(query.trim().toLowerCase()),
      ),
    [chats, query, filter],
  );
  const showChatSkeletons = loading && chats.length === 0;
  const openMenu = (chat: Chat, x: number, y: number) =>
    setMenu({
      chat,
      x: Math.min(x, window.innerWidth - 290),
      y: Math.min(y, window.innerHeight - 360),
    });
  const cancelPress = () => {
    if (press.current) window.clearTimeout(press.current.timer);
    press.current = null;
  };
  useEffect(() => {
    if (!menu) return;
    const closeOutside = (event: PointerEvent) => {
      if (!(event.target as Element).closest?.(".group-menu")) {
        setMenu(null);
        setColorPickerId(null);
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [menu]);
  return (
    <div className="chat-list" onClick={() => menu && setMenu(null)}>
      <header className="chat-list-header">
        <div className="header-row">
          <h2>Чаты Avito</h2>
          <div className="avito-header-actions">

            {onOpenTeacherAssistant && (
              <button
                className="logout-icon-button teacher-assistant-open-button"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenTeacherAssistant();
                }}
                aria-label="AI-помощник по преподавателям"
                title="AI-помощник по преподавателям"
              >
                <Sparkles size={20} />
              </button>
            )}
            <button
              className="logout-icon-button"
              onClick={onLogout}
              aria-label="Выйти"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
        {onlineUsers.length > 0 && (
          <div
            className="avito-online-staff"
            aria-label="Сейчас в Avito Chat"
          >
            {onlineUsers.map((user) => (
              <div className="avito-online-staff-badge" key={user.client_id}>
                <span className="avito-online-staff-avatar">
                  <Avatar src={user.avatar} name={user.name} imageAlt="" />
                </span>
                <span>{user.name}</span>
              </div>
            ))}
          </div>
        )}
        <div className="search-container">
          <Search className="search-icon" size={17} />
          <input
            className="search-input"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              onSearchChange?.(e.target.value);
            }}
            maxLength={256}
            placeholder="Поиск"
          />
        </div>
        <div className="group-filters">
          <button
            className={filter === "all" ? "active" : ""}
            onClick={() => setFilter("all")}
          >
            Все
          </button>
          <button
            className={filter === "unread" ? "active" : ""}
            onClick={() => setFilter("unread")}
          >
            Непрочитанные
            {chats.some((chat) => chat.unreadCount > 0) && (
              <span className="filter-unread-count">
                {chats.filter((chat) => chat.unreadCount > 0).length}
              </span>
            )}
          </button>
          <button
            className={filter === "none" ? "active" : ""}
            onClick={() => setFilter("none")}
          >
            Без группы
          </button>
          {groups.map((g) => (
            <button
              key={g.id}
              className={filter === g.id ? "active" : ""}
              onClick={() => setFilter(g.id)}
            >
              <i className={`group-dot ${g.color}`} />
              {g.name}
            </button>
          ))}
        </div>
      </header>
      <div
        className="avito-chat-items"
        onScroll={(event) => {
          const element = event.currentTarget;
          if (
            hasMore &&
            !loadingMore &&
            element.scrollHeight - element.scrollTop - element.clientHeight <
              240
          )
            onLoadMore?.();
        }}
      >
        {showChatSkeletons &&
          Array.from({ length: 6 }, (_, index) => (
            <div
              className="chat-item chat-item-skeleton"
              aria-hidden="true"
              key={index}
            >
              <div className="chat-avatar skeleton-block" />
              <div className="chat-info">
                <div className="chat-header">
                  <span className="skeleton-line skeleton-line-title" />
                  <span className="skeleton-line skeleton-line-time" />
                </div>
                <span className="skeleton-line skeleton-line-message" />
              </div>
            </div>
          ))}
        {!loading && filtered.length === 0 && (
          <div className="avito-empty-list">Чатов пока нет</div>
        )}
        {filtered.map((chat) => (
          <button
            key={chat.id}
            className={`chat-item ${selectedChatId === chat.id ? "active" : ""}`}
            style={{
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
            }}
            draggable={false}
            onDragStart={(event) => event.preventDefault()}
            onClick={() => {
              if (suppressClick.current) {
                suppressClick.current = false;
                return;
              }
              onSelect(chat.id);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              openMenu(chat, e.clientX, e.clientY);
            }}
            onPointerDown={(e) => {
              if (e.pointerType === "mouse") return;
              const x = e.clientX,
                y = e.clientY;
              press.current = {
                x,
                y,
                timer: window.setTimeout(() => {
                  suppressClick.current = true;
                  openMenu(chat, x, y);
                }, 500),
              };
            }}
            onPointerMove={(e) => {
              if (
                press.current &&
                Math.hypot(
                  e.clientX - press.current.x,
                  e.clientY - press.current.y,
                ) > 10
              )
                cancelPress();
            }}
            onPointerUp={cancelPress}
            onPointerCancel={cancelPress}
          >
            <div className="chat-avatar">
              <div className="chat-avatar-media">
                <Avatar src={chat.avatar} name={chat.name} />
              </div>
            </div>
            <div className="chat-info">
              <div className="chat-header">
                <div className="chat-name-container">
                  {(chat.groups || []).length > 0 && (
                    <span
                      className="chat-group-dots"
                      aria-label="Группы клиента"
                    >
                      {(chat.groups || []).slice(0, 3).map((g) => (
                        <i
                          key={g.id}
                          className={`group-dot ${g.color}`}
                          title={g.name}
                        />
                      ))}
                      {(chat.groups || []).length > 3 && (
                        <span>+{(chat.groups || []).length - 3}</span>
                      )}
                    </span>
                  )}
                  <span className="chat-name">{chat.name}</span>
                </div>
                <span className="chat-time-stack">
                  <span className="chat-time">
                    {time(chat.lastMessage?.created_at)}
                  </span>
                  {chat.lastMessage?.sender === "me" && (
                    <span
                      className={`chat-list-read-status ${chat.lastMessage.is_read ? "is-read" : ""}`}
                      aria-label={
                        chat.lastMessage.is_read
                          ? "Прочитано в Avito"
                          : "Не прочитано в Avito"
                      }
                    >
                      {chat.lastMessage.is_read ? (
                        <CheckCheck size={13} />
                      ) : (
                        <Check size={13} />
                      )}
                    </span>
                  )}
                </span>
              </div>
              <div className="chat-preview">
                <span className="last-message">
                  {chat.id !== selectedChatId &&
                  draftsByChat[chat.id]?.trim() ? (
                    <>
                      <span className="draft-label">Черновик: </span>
                      <span className="chat-preview-text">
                        {draftsByChat[chat.id].trim()}
                      </span>
                    </>
                  ) : (
                    <>
                      {chat.lastMessage?.sender === "me" && (
                        <span className="you-label">Вы:</span>
                      )}
                      <span className="chat-preview-text">
                        {chat.lastMessage?.text ||
                          chat.context?.title ||
                          (chat.context?.is_profile ? "В профиль" : null) ||
                          "Нет сообщений"}
                      </span>
                    </>
                  )}
                </span>
                {chat.unreadCount > 0 && (
                  <span className="unread-badge">{chat.unreadCount}</span>
                )}
              </div>
            </div>
          </button>
        ))}
        {loadingMore && (
          <div className="chat-list-refresh" role="status">
            <span className="chat-list-refresh-spinner" />
            <span>Обновление</span>
          </div>
        )}
      </div>
      {menu && (
        <div
          className="group-menu"
          style={{ left: menu.x, top: menu.y }}
          onClick={(e) => {
            e.stopPropagation();
            setColorPickerId(null);
          }}
        >
          <strong>Группы клиента</strong>
          {groups.map((g) => (
            <div
              className={`group-menu-row ${colorPickerId === g.id ? "color-picker-open" : ""}`}
              key={g.id}
            >
              <label>
                <input
                  type="checkbox"
                  checked={(menu.chat.groups || []).some((x) => x.id === g.id)}
                  onChange={() => {
                    const current = menu.chat.groups || [];
                    const next = current.some((x) => x.id === g.id)
                      ? current.filter((x) => x.id !== g.id)
                      : [...current, g];
                    setMenu({ ...menu, chat: { ...menu.chat, groups: next } });
                    void onAssign(
                      menu.chat.id,
                      next.map((x) => x.id),
                    );
                  }}
                />
                <span>{g.name}</span>
              </label>
              <button
                className="group-color-trigger"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setColorPickerId((current) =>
                    current === g.id ? null : g.id,
                  );
                }}
                aria-label={`Изменить цвет группы ${g.name}`}
              >
                <i className={`group-dot ${g.color}`} />
              </button>
              <button
                onClick={() => void onDeleteGroup(g.id)}
                aria-label={`Удалить ${g.name}`}
              >
                <Trash2 size={14} />
              </button>
              {colorPickerId === g.id && (
                <div
                  className="group-color-picker"
                  role="group"
                  aria-label={`Выбор цвета: ${g.name}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {colors.map((color) => (
                    <button
                      type="button"
                      key={color}
                      className={color === g.color ? "selected" : ""}
                      onClick={() => {
                        setColorPickerId(null);
                        void onUpdateGroup?.(g.id, { color });
                      }}
                      aria-label={`Цвет ${color}`}
                    >
                      <i className={`group-dot ${color}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="group-create-form">
            <input
              maxLength={64}
              value={newGroup}
              onChange={(e) => setNewGroup(e.target.value)}
              placeholder="Новая группа"
            />
            <button
              aria-label="Добавить группу"
              disabled={!newGroup.trim()}
              onClick={async () => {
                const name = newGroup.trim();
                setNewGroup("");
                try {
                  await onCreateGroup(
                    name,
                    colors[groups.length % colors.length],
                  );
                } catch {
                  setNewGroup((current) => current || name);
                }
              }}
            >
              <Plus size={15} />
            </button>
          </div>
        </div>
      )}
      {quickOpen && createPortal(
        <QuickRepliesPanel
          directions={replyDirections}
          replies={replies}
          selectedChatId={selectedChatId}
          onClose={() => setQuickOpen(false)}
          onCreateTopic={onCreateTopic}
          onUpdateTopic={onUpdateTopic}
          onDeleteTopic={onDeleteTopic}
          onCreateReply={onCreateReply}
          onUpdateReply={onUpdateReply}
          onDeleteReply={onDeleteReply}
          onInsertReply={onInsertReply}
        />,
        document.body,
      )}
      {/* The previous single-screen catalog is intentionally excluded after the step-based replacement.
        <div className="quick-overlay" onClick={() => setQuickOpen(false)}>
          <section
            ref={quickPanelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="quick-replies-title"
            className="quick-panel"
            style={{
              transform: `translate(${quickPosition.x}px, ${quickPosition.y}px)`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <header
              className="quick-panel-drag-handle"
              onPointerDown={(e) => {
                if (
                  window.innerWidth <= 700 ||
                  (e.target as Element).closest("button")
                )
                  return;
                quickDrag.current = {
                  pointerId: e.pointerId,
                  startX: e.clientX,
                  startY: e.clientY,
                  originX: quickPosition.x,
                  originY: quickPosition.y,
                };
                e.currentTarget.setPointerCapture(e.pointerId);
              }}
              onPointerMove={(e) => {
                const drag = quickDrag.current,
                  panel = quickPanelRef.current;
                if (!drag || drag.pointerId !== e.pointerId || !panel) return;
                const rect = panel.getBoundingClientRect();
                const nextX = drag.originX + e.clientX - drag.startX;
                const nextY = drag.originY + e.clientY - drag.startY;
                setQuickPosition({
                  x: Math.max(
                    nextX - rect.left,
                    Math.min(nextX, window.innerWidth - rect.right + nextX),
                  ),
                  y: Math.max(
                    nextY - rect.top,
                    Math.min(nextY, window.innerHeight - rect.bottom + nextY),
                  ),
                });
              }}
              onPointerUp={(e) => {
                if (quickDrag.current?.pointerId === e.pointerId)
                  quickDrag.current = null;
              }}
              onPointerCancel={() => {
                quickDrag.current = null;
              }}
            >
              <div>
                <h3 id="quick-replies-title">Быстрые ответы</h3>
                <p>
                  {selectedChatId
                    ? "Нажмите, чтобы вставить в черновик"
                    : "Сначала выберите чат"}
                </p>
              </div>
              <button
                className="quick-close-button"
                onClick={() => setQuickOpen(false)}
                aria-label="Закрыть"
              >
                <X size={20} />
              </button>
            </header>
            <div className="quick-directions" aria-label="Направления">
              {replyDirections.map((direction) => (
                <button
                  key={direction.id}
                  className={
                    selectedDirectionId === direction.id ? "active" : ""
                  }
                  onClick={() => {
                    setSelectedDirectionId(direction.id);
                    setSelectedTopicId(direction.topics[0]?.id || "");
                    setQuickQuery("");
                    setEditing(null);
                  }}
                >
                  {direction.name}
                </button>
              ))}
            </div>
            {!selectedDirection ? (
              <div className="quick-catalog-empty">
                Выберите направление, чтобы открыть темы и ответы
              </div>
            ) : (
              <>
                <div className="quick-topics">
                  <div className="quick-topic-list">
                    {selectedDirection.topics.map((topic) => (
                      <button
                        key={topic.id}
                        className={selectedTopicId === topic.id ? "active" : ""}
                        onClick={() => {
                          setSelectedTopicId(topic.id);
                          setQuickQuery("");
                          setEditing(null);
                        }}
                      >
                        {topic.name}
                      </button>
                    ))}
                  </div>
                  <div className="quick-topic-create">
                    <input
                      maxLength={100}
                      value={newTopic}
                      onChange={(e) => setNewTopic(e.target.value)}
                      placeholder="Новая тема"
                    />
                    <button
                      aria-label="Добавить тему"
                      disabled={!newTopic.trim() || !onCreateTopic}
                      onClick={async () => {
                        if (!onCreateTopic) return;
                        const name = newTopic.trim();
                        setNewTopic("");
                        const id = await onCreateTopic(
                          selectedDirection.id,
                          name,
                        );
                        setSelectedTopicId(id);
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                {selectedTopicId && (
                  <div className="quick-topic-actions">
                    <strong>
                      {
                        selectedDirection.topics.find(
                          (topic) => topic.id === selectedTopicId,
                        )?.name
                      }
                    </strong>
                    <button
                      onClick={async () => {
                        const topic = selectedDirection.topics.find(
                          (item) => item.id === selectedTopicId,
                        );
                        const name =
                          topic && window.prompt("Название темы", topic.name);
                        if (name?.trim() && onUpdateTopic)
                          await onUpdateTopic(selectedTopicId, name.trim());
                      }}
                    >
                      Переименовать
                    </button>
                    <button
                      onClick={async () => {
                        if (
                          !onDeleteTopic ||
                          !window.confirm("Удалить тему и все ответы в ней?")
                        )
                          return;
                        await onDeleteTopic(selectedTopicId);
                        setSelectedTopicId("");
                      }}
                    >
                      <Trash2 size={14} />
                      Удалить
                    </button>
                  </div>
                )}
                <div className="quick-search">
                  <Search size={16} />
                  <input
                    value={quickQuery}
                    onChange={(e) => setQuickQuery(e.target.value)}
                    placeholder={`Поиск в направлении «${selectedDirection.name}»`}
                  />
                </div>
                {editing !== null && (
                  <div className="quick-editor">
                    <textarea
                      maxLength={1000}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      autoFocus
                      placeholder="Текст быстрого ответа"
                    />
                    <div>
                      <button
                        onClick={() => {
                          setEditing(null);
                          setDraft("");
                        }}
                      >
                        Отмена
                      </button>
                      <button
                        disabled={
                          !draft.trim() || (!editing.id && !selectedTopicId)
                        }
                        onClick={async () => {
                          editing.id
                            ? await onUpdateReply(editing.id, draft)
                            : await onCreateReply(selectedTopicId, draft);
                          setEditing(null);
                          setDraft("");
                        }}
                      >
                        Сохранить
                      </button>
                    </div>
                  </div>
                )}
                <div className="quick-items">
                  {visibleReplies.map((reply) => (
                    <article key={reply.id}>
                      {quickQuery && (
                        <span className="quick-reply-topic-label">
                          {
                            selectedDirection.topics.find(
                              (topic) => topic.id === reply.topic_id,
                            )?.name
                          }
                        </span>
                      )}
                      <button
                        className="quick-text"
                        disabled={!selectedChatId}
                        onClick={() => {
                          onInsertReply(reply.text);
                          setQuickOpen(false);
                        }}
                      >
                        {reply.text}
                      </button>
                      <div>
                        <button
                          onClick={() => {
                            setSelectedTopicId(reply.topic_id);
                            setEditing(reply);
                            setDraft(reply.text);
                          }}
                        >
                          Изменить
                        </button>
                        <button
                          aria-label="Удалить ответ"
                          onClick={() => void onDeleteReply(reply.id)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </article>
                  ))}
                  {selectedTopicId && visibleReplies.length === 0 && (
                    <div className="quick-empty-topic">
                      В этой теме пока нет ответов
                    </div>
                  )}
                  {!selectedTopicId && (
                    <div className="quick-empty-topic">
                      Создайте или выберите тему
                    </div>
                  )}
                </div>
                <button
                  className="quick-add"
                  disabled={!selectedTopicId}
                  onClick={() => {
                    if (!selectedTopicId) return;
                    setEditing({
                      id: "",
                      text: "",
                      topic_id: selectedTopicId,
                      sort_order: replies.length,
                    });
                    setDraft("");
                  }}
                >
                  <Plus size={17} />
                  Добавить ответ
                </button>
              </>
            )}
          </section>
        </div>
      */}
    </div>
  );
};
export default ChatList;
